import React from 'react'
import { FormControl, Icon } from '@mui/material';
import { withStyles } from '@mui/styles'
import { Tooltip, Typography, Box } from '@mui/material'

import { listInputStyles } from './list-input.styles'
import { BasicTable } from '../basic-table'

/**
 * @typedef {import('../basic-table/basic-table.component').ColumnConfig} ColumnConfig
 */

/**
 * @typedef {object} ListInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {any[]} [values=[]] - Un array de valores (no utilizado actualmente en la lógica del componente).
 * @property {boolean} [multiple=false] - Prop recibida pero no utilizada actualmente en la lógica del componente.
 * @property {boolean} [readOnly=false] - Si es `true`, el componente se renderiza en modo de solo lectura (aunque actualmente ambos modos renderizan igual).
 * @property {boolean} [selectAll=false] - Prop recibida pero no utilizada actualmente en la lógica del componente.
 * @property {string} [title] - El título que se muestra sobre la tabla.
 * @property {string} [tooltip] - Un texto de ayuda que se muestra en un tooltip junto al título.
 * @property {ColumnConfig[]} subtitle - La configuración de columnas para el `BasicTable`.
 * @property {object[]} value - El array de datos a mostrar en la tabla.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título.
 * @property {boolean} [required] - Si es `true`, el campo se marca como requerido.
 * @property {boolean} [disabled] - Si es `true`, el campo estará deshabilitado.
 */

/**
 * Un componente de entrada que muestra una lista de datos en formato de tabla.
 * 
 * Renderiza los datos usando el componente `BasicTable`, permitiendo visualizar
 * información estructurada en formato tabular. Incluye soporte para un título
 * con tooltip opcional y muestra '--' cuando no hay datos.
 * 
 * **Nota:** A pesar del nombre "Input", este componente es principalmente de visualización
 * y no proporciona funcionalidad de edición directa.
 * 
 * @component
 * @example
 * const items = [
 *   { id: 1, name: 'Producto A', price: 100 },
 *   { id: 2, name: 'Producto B', price: 200 },
 * ];
 * 
 * const columns = [
 *   { label: 'ID', key: 'id' },
 *   { label: 'Nombre', key: 'name' },
 *   { label: 'Precio', key: 'price', align: 'right' },
 * ];
 * 
 * <ListInput
 *   title="Lista de Productos"
 *   tooltip="Estos son los productos seleccionados"
 *   subtitle={columns}
 *   value={items}
 * />
 * 
 * @param {ListInputProps} props - Las props del componente ListInput.
 */
export const ListInput = React.memo(
  withStyles(listInputStyles)(
    ({ classes, values = [], multiple = false, readOnly = false, selectAll = false, ...props }) => {
      const typographyVariant = props.fontSize === 'small' ? '2' : '1'
      const value = props.value

      const getValue = () => {
        if (value.length > 0) {
          return <BasicTable config={props.subtitle} data={value} size="small" />
        } else return '--'
      }

      return (
        <FormControl
          variant="outlined"
          className={classes.root}
          required={props.required}
          disabled={props.disabled}
        >
          <Box display="flex" flexDirection="horizontal">
            {props.title && (
              <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
                {props.title}
              </Typography>
            )}
            {props.tooltip && (
              <span style={{ fontSize: 16, marginLeft: 4 }}>
                <Tooltip
                  placement="top"
                  title={
                    <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
                      {props.tooltip}
                    </Typography>
                  }
                >
                  <Icon fontSize="inherit">help_outline</Icon>
                </Tooltip>
              </span>
            )}
          </Box>
          {readOnly ? (
            <Typography variant={`body${typographyVariant}`} color="textSecondary">
              {getValue()}
            </Typography>
          ) : (
            <Typography variant={`body${typographyVariant}`} color="textSecondary">
              {getValue()}
            </Typography>
          )}
        </FormControl>
      )
    }
  )
)
